import { KeyEvent, Keys } from "excalibur";
import { BaseFlow } from "../engine/baseFlow";
import { MyLevel } from "../level";
import { CardPack } from "../types";
import { getPack, packSize } from "../constants";

export type ShopResult =
  | { purchased: true; pack: CardPack; cardsAdded: number }
  | { purchased: false };

export class ShopFlow extends BaseFlow<ShopResult> {
  level: MyLevel;
  private isOutOfCards: boolean;

  constructor(level: MyLevel, isOutOfCards: boolean = false) {
    super(level.engine.clock);
    this.level = level;
    this.isOutOfCards = isOutOfCards;
  }

  override async Execute() {
    console.log("Shop flow started");

    // Show the shop UI
    this.level.hudInterface.showShop = true;
    this.level.hudInterface.isShopOpenDueToOutOfCards = this.isOutOfCards;

    this.closeOnComplete(
      // Listen for ESC key to close shop
      this.level.engine.input.keyboard.on("release", (evt: KeyEvent) => {
        if (evt.key === Keys.Esc) {
          this.Complete({ purchased: false });
        }
      }),

      // Listen for pack purchase events from HUD
      this.level.hudInterface.events.on("buypack", (evt) => {
        this.handlePackPurchase(evt.context.packName);
      }),

      // Listen for shop close events from HUD (X button or backdrop click)
      this.level.hudInterface.events.on("shopclose", () => {
        this.Complete({ purchased: false });
      }),
    );
  }

  private handlePackPurchase(packName: string): void {
    const pack = getPack(packName);
    if (!pack) {
      console.error(`Pack not found: ${packName}`);
      this.Complete({ purchased: false });
      return;
    }

    // Validate purchase
    if (!this.canBuyPack(pack)) {
      console.log(
        "Cannot buy pack - insufficient funds or missing requirements",
      );
      return; // Don't complete, let user try again or ESC out
    }

    // Process purchase
    this.level.gameState.money -= pack.cost;

    // Add pack to deck (generates cards automatically)
    this.level.cards.addPack(pack, packSize);

    this.Complete({
      purchased: true,
      pack,
      cardsAdded: packSize,
    });
  }

  override Complete(result: ShopResult) {
    // Hide the shop UI
    this.level.hudInterface.showShop = false;
    this.level.hudInterface.isShopOpenDueToOutOfCards = false;
    super.Complete(result);
  }

  private canBuyPack(pack: CardPack): boolean {
    // Check money
    if (this.level.gameState.money < pack.cost) {
      return false;
    }

    // Check requirements
    return pack.required.every((resource) =>
      this.level.gameState.seenResources.has(resource),
    );
  }
}
